import { isTestGame } from './game-visibility.js';

export const SEO_HOME_TITLE = '摸鱼岛 — 在线小游戏，即点即玩';
export const SEO_DESCRIPTION =
  '摸鱼岛是你的小游戏休息站，发现益智、休闲、经营和对战游戏，无需下载安装，打开浏览器即可开始。忙里偷闲，快乐靠岸。';
export interface SeoGame {
  id: string;
  slug: string;
  name: string;
  description: string;
  longDescription: string;
  categories: string[];
  mode: string;
  controls: string;
  free: boolean;
  published: boolean;
  coverImage: string;
  updatedAt: string;
}
export interface PageMetadata {
  title: string;
  description: string;
  canonicalPath: string | null;
  index: boolean;
  image: string;
}
export const gamePath = (slug: string) => '/game/' + encodeURIComponent(slug);
export function publicSeoGame(game: SeoGame) {
  return game.published && !isTestGame(game);
}
export function cleanDescription(value: string) {
  return value.replace(/\s+/g, ' ').trim().slice(0, 180);
}
const privatePages: Record<string, string> = {
  '/login': '登录与注册',
  '/favorites': '我的收藏',
  '/recent': '最近游玩',
  '/onboarding': '设置个人资料',
  '/account/profile': '我的主页',
  '/account/profile/edit': '编辑个人资料',
  '/account/avatar': '设置头像',
  '/community': '岛民广场',
  '/rankings': '排行榜',
  '/achievements': '我的成就',
  '/wishes': '心愿榜',
  '/membership': '会员',
  '/verify-email': '邮箱验证',
  '/invite': '邀请好友',
  '/contact': '联系摸鱼岛',
  '/notifications': '通知',
};
const adminPages = new Set([
  '',
  '/games',
  '/achievements',
  '/rankings',
  '/analytics',
  '/analytics/visitors',
  '/analytics/traffic',
  '/analytics/games',
  '/analytics/sources',
  '/support',
  '/feedback',
  '/community',
  '/operations',
  '/announcements',
  '/users',
  '/referrals',
  '/settings',
  '/release-tokens',
  '/diagnostics',
  '/logs',
  '/login',
]);
export function clientPageTitle(pathname: string): string | null {
  if (privatePages[pathname]) return privatePages[pathname];
  if (/^\/play\/[^/]+$/.test(pathname)) return '开始游戏';
  if (/^\/messages\/[^/]+$/.test(pathname)) return '私信';
  if (/^\/i\/[^/]+$/.test(pathname)) return '邀请好友';
  if (
    pathname.startsWith('/admin') &&
    (adminPages.has(pathname.slice(6)) || /^\/admin\/analytics\/games\/[^/]+$/.test(pathname))
  )
    return '管理后台';
  return null;
}
export function pageMetadata(pathname: string, game?: SeoGame, search = ''): PageMetadata {
  const image = '/brand/island-2026/mark.webp';
  const base = {
    title: '页面不存在｜摸鱼岛',
    description: '这个页面暂时无法访问，请返回摸鱼岛游戏库。',
    canonicalPath: null,
    index: false,
    image,
  };
  if (pathname === '/' || pathname === '/library')
    return {
      title: pathname === '/' ? SEO_HOME_TITLE : '游戏库 — 发现在线小游戏｜摸鱼岛',
      description: SEO_DESCRIPTION,
      canonicalPath: pathname,
      index: !new URLSearchParams(search).get('q')?.trim(),
      image,
    };
  if (game && publicSeoGame(game))
    return {
      title: game.name + ' — 在线小游戏｜摸鱼岛',
      description: cleanDescription(
        game.description ||
          game.longDescription ||
          `${game.name}，在摸鱼岛了解玩法与操作，打开浏览器即可游玩。`,
      ),
      canonicalPath: gamePath(game.slug),
      index: true,
      image: game.coverImage || image,
    };
  if (game)
    return { ...base, title: '游戏预览｜摸鱼岛', description: '登录后查看有权限访问的游戏。' };
  const title = clientPageTitle(pathname);
  return title
    ? { ...base, title: title + '｜摸鱼岛', description: '摸鱼岛，你的小游戏休息站。' }
    : base;
}
export function safeImageUrl(value: string, origin: string): string | null {
  try {
    const url = new URL(value, origin);
    return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password
      ? url.href
      : null;
  } catch {
    return null;
  }
}
